/**
 * rs-charm-builder-core.js
 *
 * Shared base class for PDP and cart charm builders.
 * Exposes window.RsCharmBuilderBase — extended by:
 *   rs-product-charm-builder.js  (PDP)
 *   rs-cart-charm-builder.js     (cart edit drawer)
 */

(() => {
  'use strict';

  if (window.RsCharmBuilderBase) return;

  const COLLECTION_QUERY = `
    query CollectionProducts($handle: String!, $first: Int!, $after: String) {
      collection(handle: $handle) {
        products(first: $first, after: $after) {
          edges { node {
            title
            availableForSale
            featuredImage { url }
            variants(first: 1) { edges { node { id } } }
          } }
          pageInfo { hasNextPage endCursor }
        }
      }
    }`;

  const esc = (str) => str.replace(/"/g, '&quot;');

  window.RsCharmBuilderBase = class extends HTMLElement {
    get _root() { return this; }

    _beforeUIBuild() {}
    _afterUIBuild() {}
    _onSlotChange() {}

    _stepLabel(step) {
      if (!step || step < 1) return '';
      return `<span class="rs-step rs-step--${step}">step ${step}</span> `;
    }

    _buildUI() {
      this._beforeUIBuild();
      this._root.innerHTML = `
        <h4 class="rs-charm__heading">${this._stepLabel(this.config.headingStep)}${this.config.mainHeading}</h4>
        <div class="rs-charm"></div>`;

      const wrap = this._root.querySelector('.rs-charm');
      this._buildPrimaryTabs(wrap);
      this._buildPanels(wrap);
      this._buildSelectedSection(wrap);
      this._afterUIBuild();
      this._initSlots();
      this._activatePanel(0);
    }

    _buildPrimaryTabs(wrap) {
      if (this.config.categories.length <= 1) {
        this._tabBar = null;
        return;
      }

      const tabs = this.config.categories.map((cat, i) => `
        <button type="button" role="tab" class="rs-charm__tab${i === 0 ? ' is-active' : ''}"
          data-tab="${i}" aria-selected="${i === 0}">${cat.title}</button>
      `).join('');

      this._tabBar = document.createElement('div');
      this._tabBar.className = 'rs-charm__tabs';
      this._tabBar.setAttribute('role', 'tablist');
      this._tabBar.innerHTML = tabs;
      wrap.appendChild(this._tabBar);

      this._tabBar.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tab]');
        if (btn) this._activatePanel(parseInt(btn.dataset.tab, 10));
      });
    }

    _buildPanels(wrap) {
      this._panelsWrap = document.createElement('div');
      this._panelsWrap.className = 'rs-charm__panels';
      this._panelsWrap.innerHTML = this.config.categories.map((cat, i) => `
        <div class="rs-charm__panel${i === 0 ? ' is-active' : ''}" data-panel="${i}" data-type="${cat.type}"></div>
      `).join('');
      wrap.appendChild(this._panelsWrap);
    }
    
    get _slotLabel() {
      return this.config.slotLabel || 'Charm';
    }

    _buildSelectedSection(wrap) {
      this._selectedWrap = document.createElement('div');
      this._selectedWrap.className = 'rs-charm__selected';
      this._selectedWrap.innerHTML = `
        <p class="rs-charm__selected-title">${this.config.selectedCharmsHeading}</p>
        <div class="rs-charm__slots"></div>
        <p class="rs-charm__selected-hint">Click and drag to rearrange your ${this._slotLabel.toLowerCase()}s</p>`;
      this._slotList = this._selectedWrap.querySelector('.rs-charm__slots');
      wrap.appendChild(this._selectedWrap);
    }

    _activatePanel(index) {
      if (this._tabBar) {
        this._tabBar.querySelectorAll('.rs-charm__tab').forEach((t, i) => {
          t.classList.toggle('is-active', i === index);
          t.setAttribute('aria-selected', i === index);
        });
      }

      this._panelsWrap.querySelectorAll('.rs-charm__panel').forEach((p, i) => {
        p.classList.toggle('is-active', i === index);
      });

      this._activeCat = index;

      if (!this._renderedPanels.has(index)) {
        const panel = this._panelsWrap.querySelector(`[data-panel="${index}"]`);
        const cat = this.config.categories[index];
        this._buildPanelContent(panel, cat, index);
        this._renderedPanels.add(index);
      }

      this._clearPending();
      this._updateBadges();
    }

    _buildPanelContent(panel, category, catIdx) {
      if (category.type === 'picker-with-search') {
        this._buildPickerWithSearch(panel, category, catIdx);
      } else {
        this._buildSubtabPanel(panel, category, catIdx);
        if (category.type === 'picker-with-input') {
          this._buildTextInput(panel, category, catIdx);
        }
      }
    }

    _buildSubtabPanel(panel, category, catIdx) {
      const { subcategories: subs } = category;
      const showSubtabs = subs.length > 1;
      const isPending = category.type === 'picker-with-input';

      const wrapper = document.createElement('div');
      wrapper.className = 'rs-charm__subtabs-wrap';

      if (showSubtabs) {
        const labelHtml = category.subcategoryLabel
          ? `<p class="rs-charm__subtabs-label">${category.subcategoryLabel}</p>` : '';

        const subtabBtns = subs.map((sub, i) => `
          <button type="button" role="tab" class="rs-charm__subtab${i === 0 ? ' is-active' : ''}"
            data-subtab="${i}" aria-selected="${i === 0}">${sub.title}</button>
        `).join('');

        wrapper.insertAdjacentHTML('beforeend', `
          ${labelHtml}
          <div class="rs-charm__subtabs" role="tablist">${subtabBtns}</div>`);

        wrapper.querySelector('.rs-charm__subtabs').addEventListener('click', (e) => {
          const btn = e.target.closest('[data-subtab]');
          if (btn) this._activateSubtab(wrapper, parseInt(btn.dataset.subtab, 10), catIdx);
        });
      }

      subs.forEach((sub, i) => {
        const subpanel = document.createElement('div');
        subpanel.className = `rs-charm__subpanel${i === 0 ? ' is-active' : ''}`;
        subpanel.dataset.subpanel = i;

        const grid = document.createElement('div');
        grid.className = 'rs-charm__grid';
        grid.dataset.gridKey = `${catIdx}-${i}`;
        subpanel.appendChild(grid);

        if (sub.items) {
          this._renderGrid(grid, sub.items, isPending);
        } else {
          grid.innerHTML = '<div class="rs-charm__loading">Loading…</div>';
        }
        wrapper.appendChild(subpanel);
      });

      panel.appendChild(wrapper);
      this._activeSubcat[catIdx] = 0;
    }

    _activateSubtab(wrapper, subIdx, catIdx) {
      wrapper.querySelectorAll('.rs-charm__subtab').forEach((t, i) => {
        t.classList.toggle('is-active', i === subIdx);
        t.setAttribute('aria-selected', i === subIdx);
      });
      wrapper.querySelectorAll('.rs-charm__subpanel').forEach((p, i) => {
        p.classList.toggle('is-active', i === subIdx);
      });
      this._activeSubcat[catIdx] = subIdx;
      this._clearPending();

      if (this.config.categories[catIdx].type === 'picker-with-input') {
        const panel = this._panelsWrap.querySelector(`[data-panel="${catIdx}"]`);
        const mto = this._getMtoRules(catIdx, subIdx);
        if (mto && panel) this._applyMto(mto, panel);
      }
    }

    _buildPickerWithSearch(panel, category, catIdx) {
      panel.insertAdjacentHTML('beforeend', `
        <div class="rs-charm__search-wrap">
          <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-search" viewBox="0 0 24 24">
            <g stroke="currentColor"><path d="M10.85 2c2.444 0 4.657.99 6.258 2.592A8.85 8.85 0 1 1 10.85 2ZM17.122 17.122 22 22"></path></g>
          </svg>
          <input type="search" class="rs-charm__search" placeholder="Search..." data-icon-search>
        </div>
        <div class="rs-charm__icon-filters">
          <button type="button" class="rs-charm__icon-filter is-active" data-filter="all">All</button>
          ${category.subcategories.map((sub, i) =>
            `<button type="button" class="rs-charm__icon-filter" data-filter="${i}">${sub.title}</button>`
          ).join('')}
        </div>
        <div class="rs-charm__icon-categories"></div>`);

      const filters = panel.querySelector('.rs-charm__icon-filters');
      filters.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-filter]');
        if (!btn) return;
        filters.querySelectorAll('.rs-charm__icon-filter').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const f = btn.dataset.filter;
        panel.querySelectorAll('.rs-charm__icon-category').forEach(sec => {
          sec.classList.toggle('is-hidden', f !== 'all' && sec.dataset.catIdx !== f);
        });
      });

      const container = panel.querySelector('.rs-charm__icon-categories');
      category.subcategories.forEach((sub, i) => {
        const section = document.createElement('div');
        section.className = 'rs-charm__icon-category';
        section.dataset.catIdx = String(i);
        section.dataset.catTitle = sub.title.toLowerCase();
        section.innerHTML = `<div class="rs-charm__icon-category-title">${sub.title}</div>
          <div class="rs-charm__grid" data-grid-key="${catIdx}-${i}"></div>`;

        const grid = section.querySelector('.rs-charm__grid');
        if (sub.items) {
          this._renderGrid(grid, sub.items, false);
        } else {
          grid.innerHTML = '<div class="rs-charm__loading">Loading…</div>';
        }
        container.appendChild(section);
      });

      const searchInput = panel.querySelector('[data-icon-search]');
      searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim().toLowerCase();
        panel.querySelectorAll('.rs-charm__icon-category').forEach(sec => {
          const catTitle = sec.dataset.catTitle || '';
          let hasVisible = false;
          sec.querySelectorAll('.rs-charm__grid-item').forEach(item => {
            const t = (item.dataset.title || '').toLowerCase();
            const match = !query || t.includes(query) || catTitle.includes(query);
            item.style.display = match ? '' : 'none';
            if (match) hasVisible = true;
          });
          sec.classList.toggle('is-hidden', !hasVisible);
        });
      });
    }

    _buildTextInput(panel, category, catIdx) {
      if (category.instructionMessage) {
        const html = this._richTextToHtml(category.instructionMessage);
        panel.insertAdjacentHTML('beforeend',
          `<div class="rs-charm__instruction">${html}</div>`);
      }

      const inputId = this._textInputId(catIdx);
      const mtoRules = this._getMtoRules(catIdx, 0);
      const isCaseSensitive = mtoRules?.caseSensitive?.[0] === 'true';
      let inputLabel = category.textInputLabel || 'Customization';
      const addLabel = inputLabel;

      if (isCaseSensitive) {
        inputLabel += ' <span class="rs-mto__label-sub">(Case Sensitive)</span>:';
      } else {
        inputLabel += ':';
      }
      panel.insertAdjacentHTML('beforeend', `
        <div class="rs-charm__input-wrap">
          <label class="rs-charm__input-label" for="${inputId}">
            ${inputLabel}
          </label>
          <div class="rs-charm__input-row">
            <input type="text" id="${inputId}" class="rs-charm__input-field"
              placeholder="${category.textInputPlaceholder}"
              autocomplete="off" data-text-input>
            <button type="button" class="rs-charm__input-add" data-text-add disabled aria-label="Add ${addLabel}">Add</button>
          </div>
          <div class="rs-charm__subtext">
            <p class="rs-charm__input-limit"><span data-max-chars>100</span> characters max</p>
          </div>
        </div>`);

      const mto = this._getMtoRules(catIdx, 0);
      if (mto) this._applyMto(mto, panel);

      const input = panel.querySelector('[data-text-input]');
      const addBtn = panel.querySelector('[data-text-add]');

      input.addEventListener('input', () => this._validateInput(panel));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !addBtn.disabled) {
          e.preventDefault();
          this._addTextCharm(panel, catIdx);
        }
      });
      addBtn.addEventListener('click', () => this._addTextCharm(panel, catIdx));
    }

    _textInputId(catIdx) {
      return `rs-charm-text-${catIdx}`;
    }

    _renderGrid(container, items, isPendingMode) {
      container.innerHTML = items
        .filter(item => item.available || !item.variantId)
        .map(item => {
          const vid = item.variantId ? ` data-variant-id="${item.variantId}"` : '';
          let visual;
          if (item.imageUrl) {
            visual = `<div class="rs-charm__img-wrap is-loading">
              <div class="rs-charm__img-loader"></div>
              <img class="rs-charm__grid-item-img" src="${item.imageUrl}" alt="${esc(item.title)}" loading="lazy">
            </div>`;
          } else {
            visual = `<div class="rs-charm__img-wrap">
              <div class="rs-charm__grid-item-fallback">${item.title.charAt(0)}</div>
            </div>`;
          }
          return `<button type="button" class="rs-charm__grid-item" data-title="${esc(item.title)}"${vid}>
            ${visual}<span class="rs-charm__grid-item-title">${item.title}</span>
          </button>`;
        }).join('');

      container.querySelectorAll('.rs-charm__grid-item-img').forEach(img => {
        const wrap = img.closest('.rs-charm__img-wrap');
        img.addEventListener('load', () => wrap.classList.remove('is-loading'));
        img.addEventListener('error', () => {
          wrap.classList.remove('is-loading');
          wrap.innerHTML = `<div class="rs-charm__grid-item-fallback">${(img.alt || '?').charAt(0)}</div>`;
        });
        if (img.complete) wrap.classList.remove('is-loading');
      });

      const [gridCatIdx, gridSubIdx] = (container.dataset.gridKey || '').split('-').map(Number);

      // Track the current pending mode so the single delegated listener below
      container._pendingMode = isPendingMode;

      if (!container._clickBound) {
        container._clickBound = true;
        container.addEventListener('click', (e) => {
          const btn = e.target.closest('.rs-charm__grid-item');
          if (!btn) return;
          const currentItems = this.config.categories[gridCatIdx]?.subcategories?.[gridSubIdx]?.items || [];
          const item = currentItems.find(it =>
            it.title === btn.dataset.title && (String(it.variantId || '') === (btn.dataset.variantId || ''))
          );
          if (!item) return;
          if (container._pendingMode) {
            this._setPending(btn, item);
          } else {
            this._addToSlot(item, gridCatIdx, gridSubIdx);
          }
        });
      }
      this._updateBadges();
    }

    /* ── Data Fetching (Storefront API) ────────── */

    _fetchAllCollections() {
      const fetches = [];
      this.config.categories.forEach((cat, ci) => {
        cat.subcategories.forEach((sub, si) => {
          if (sub.sourceType === 'collection' && sub.collectionHandle) {
            fetches.push(this._fetchCollection(sub, ci, si));
          }
        });
      });
      if (fetches.length) Promise.all(fetches);
    }

    async _fetchCollection(sub, catIdx, subIdx) {
      const { collectionHandle: handle } = sub;

      if (this._cache[handle]) {
        sub.items = this._cache[handle];
        this._refreshGrid(catIdx, subIdx);
        return;
      }

      const allItems = [];
      let cursor = null;
      let isFirstPage = true;

      try {
        do {
          const data = await this._storefrontQuery(COLLECTION_QUERY, {
            handle, first: 50, after: cursor
          });

          const products = data?.collection?.products;
          if (!products) break;

          const pageItems = products.edges.map(({ node }) => ({
            title: node.title,
            imageUrl: node.featuredImage?.url || '',
            variantId: this._stripGid(node.variants?.edges[0]?.node?.id),
            available: node.availableForSale
          }));

          allItems.push(...pageItems);

          if (isFirstPage) {
            sub.items = [...allItems];
            this._refreshGrid(catIdx, subIdx);
            isFirstPage = false;
          }

          if (products.pageInfo.hasNextPage) {
            cursor = products.pageInfo.endCursor;
          } else {
            cursor = null;
          }
        } while (cursor);

        sub.items = allItems;
        this._cache[handle] = allItems;
        this._refreshGrid(catIdx, subIdx);
      } catch (err) {
        console.error('[RsCharmBuilderBase] Collection fetch error:', err);
      }
    }

    async _storefrontQuery(query, variables) {
      const cfg = window.RSStorefrontApiConfig;
      if (!cfg?.token) throw new Error('Storefront API config missing');

      const url = `${location.origin}/api/${cfg.apiVersion || '2025-10'}/graphql.json`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token
        },
        body: JSON.stringify({ query, variables })
      });

      if (!res.ok) throw new Error(`Storefront API ${res.status}`);
      const json = await res.json();
      if (json.errors) throw new Error(JSON.stringify(json.errors));
      return json.data;
    }

    _stripGid(gid) {
      return gid ? gid.split('/').pop() : null;
    }

    _refreshGrid(catIdx, subIdx) {
      const grid = this._root.querySelector(`[data-grid-key="${catIdx}-${subIdx}"]`);
      if (!grid) return;
      const cat = this.config.categories[catIdx];
      const sub = cat.subcategories[subIdx];
      if (sub.items) this._renderGrid(grid, sub.items, cat.type === 'picker-with-input');
    }

    /* ── Pending Charm (picker-with-input) ──────── */

    _setPending(btnEl, item) {
      this._clearPending();
      btnEl.classList.add('is-pending');
      this._pendingCharm = { el: btnEl, item };

      const catIdx = this._activeCat;
      const subIdx = this._activeSubcat[catIdx] || 0;
      const mto = this._getMtoRules(catIdx, subIdx);
      const panel = this._panelsWrap.querySelector(`[data-panel="${catIdx}"]`);

      if (mto && panel) this._applyMto(mto, panel);

      const input = panel?.querySelector('[data-text-input]');
      if (input) {
        input.value = '';
        input.focus();
        this._validateInput(panel);
      }
    }

    _clearPending() {
      if (this._pendingCharm) {
        this._pendingCharm.el.classList.remove('is-pending');
        this._pendingCharm = null;
      }
    }

    _addTextCharm(panel, catIdx) {
      const input = panel.querySelector('[data-text-input]');
      const text = input?.value.trim();
      if (!text) return;

      const subIdx = this._activeSubcat[catIdx] ?? 0;
      const pending = this._pendingCharm;
      if (pending) {
        this._addToSlot({
          title: text,
          itemTitle: pending.item.title,
          imageUrl: pending.item.imageUrl,
          variantId: pending.item.variantId,
          customization: text
        }, catIdx, subIdx);
        this._clearPending();
      } else {
        this._addToSlot({ title: text, itemTitle: null, imageUrl: '', variantId: null, customization: text }, catIdx, subIdx);
      }

      input.value = '';
      const addBtn = panel.querySelector('[data-text-add]');
      if (addBtn) addBtn.disabled = true;
    }

    /* ── Slot Preference ──────────────────────── */

    /* Default landing slot from restrict_to_slot(_number), not a reservation */
    _preferredSlotFor(catIdx) {
      const raw = this.config.categories?.[catIdx]?.restrictToSlot;
      if (raw == null) return null;
      const slot = parseInt(raw, 10) - 1;
      if (isNaN(slot) || slot < 0 || slot >= this.charmCount) return null;
      return slot;
    }

    /* A charm resting on its preferred slot cannot be dragged, in or out */
    _isSlotLocked(slotIdx) {
      const charm = this.slots[slotIdx];
      if (!charm) return false;
      return this._preferredSlotFor(charm._catIdx) === slotIdx;
    }

    _flashSlot(slotIdx) {
      const slotEl = this._slotList.querySelector(`[data-slot="${slotIdx}"]`);
      if (!slotEl) return;
      slotEl.classList.add('is-invalid');
      setTimeout(() => slotEl.classList.remove('is-invalid'), 600);
    }

    /* Cart edit restores only variantId — recover the category for limits/preference */
    _inferCharmIdentity(charm) {
      if (!charm || charm._catIdx !== undefined || !charm.variantId) return charm;

      const cats = this.config.categories || [];
      for (let ci = 0; ci < cats.length; ci++) {
        const subs = cats[ci].subcategories || [];
        for (let si = 0; si < subs.length; si++) {
          const items = subs[si].items || [];
          const hit = items.some(it => String(it.variantId) === String(charm.variantId));
          if (hit) {
            charm._catIdx = ci;
            charm._subIdx = si;
            return charm;
          }
        }
      }
      return charm;
    }

    /* ── Slot Management ───────────────────────── */

    _initSlots() {
      this.slots = new Array(this.charmCount).fill(null);
      this._slotList.innerHTML = this.slots.map((_, i) => `
        <div class="rs-charm__slot" data-slot="${i}" draggable="true">
          <div class="rs-charm__slot-visual">
            <span class="rs-charm__slot-fallback">Blank</span>
          </div>
          <span class="rs-charm__slot-label">${this._slotLabel} #${i + 1}</span>
        </div>`).join('');

      this._initDragDrop();
      this._updateBadges();
      this._onSlotChange();
    }

    _resizeSlots(newCount) {
      const prev = this.slots.slice(0, newCount);
      this.charmCount = newCount;
      this.slots = new Array(newCount).fill(null);

      for (var i = 0; i < prev.length; i++) {
        if (prev[i]) this.slots[i] = prev[i];
      }

      this._slotList.innerHTML = this.slots.map((_, i) => `
        <div class="rs-charm__slot" data-slot="${i}" draggable="true">
          <div class="rs-charm__slot-visual">
            <span class="rs-charm__slot-fallback">Blank</span>
          </div>
          <span class="rs-charm__slot-label">${this._slotLabel} #${i + 1}</span>
        </div>`).join('');

      for (var j = 0; j < this.slots.length; j++) {
        if (this.slots[j]) {
          var slotEl = this._slotList.querySelector('[data-slot="' + j + '"]');
          this._renderSlotFilled(slotEl, j, this.slots[j]);
        }
      }

      this._initDragDrop();
      this._updateBadges();
      this._onSlotChange();
    }

    /* Adds a charm to the next empty slot.
      When catIdx/subIdx are supplied and the category is limited, an existing charm is
      replaced instead of occupying a new slot:
        - limitToOnePerCategory    → replace any charm from the same category.
        - limitToOnePerSubcategory → replace the charm from the same subcategory.
      limitToOnePerCategory is broader, so it takes precedence when both are set.
    */
    _addToSlot(item, catIdx, subIdx) {
      const enriched = { ...item };
      if (catIdx !== undefined && !isNaN(catIdx)) {
        enriched._catIdx = catIdx;
        enriched._subIdx = subIdx;
      }

      if (enriched._catIdx !== undefined) {
        const cat = this.config.categories[enriched._catIdx];
        let replaceIdx = -1;
        if (cat?.limitToOnePerCategory) {
          replaceIdx = this.slots.findIndex(s => s && s._catIdx === enriched._catIdx);
        } else if (cat?.limitToOnePerSubcategory) {
          replaceIdx = this.slots.findIndex(
            s => s && s._catIdx === enriched._catIdx && s._subIdx === enriched._subIdx
          );
        }
        if (replaceIdx !== -1) {
          this.slots[replaceIdx] = enriched;
          const slotEl = this._slotList.querySelector(`[data-slot="${replaceIdx}"]`);
          this._renderSlotFilled(slotEl, replaceIdx, enriched);
          this._updateBadges();
          this._onSlotChange();
          return true;
        }
      }

      let targetIdx = -1;

      // Take the preferred slot, moving another category's charm aside for it.
      // Same-category occupant is left alone — repeat picks fill normally.
      const preferred = this._preferredSlotFor(enriched._catIdx);
      if (preferred !== null) {
        const occupant = this.slots[preferred];
        if (!occupant) {
          targetIdx = preferred;
        } else if (occupant._catIdx !== enriched._catIdx) {
          const freeIdx = this.slots.indexOf(null);
          if (freeIdx !== -1) {
            this.slots[freeIdx] = occupant;
            const freeEl = this._slotList.querySelector(`[data-slot="${freeIdx}"]`);
            this._renderSlotFilled(freeEl, freeIdx, occupant);
            targetIdx = preferred;
          }
        }
      }

      if (targetIdx === -1) targetIdx = this.slots.indexOf(null);
      if (targetIdx === -1) return false;
      this.slots[targetIdx] = enriched;
      const slot = this._slotList.querySelector(`[data-slot="${targetIdx}"]`);
      this._renderSlotFilled(slot, targetIdx, enriched);
      this._updateBadges();
      this._onSlotChange();
      return true;
    }

    _removeFromSlot(index) {
      if (!this.slots[index]) return;
      this.slots[index] = null;
      const slot = this._slotList.querySelector(`[data-slot="${index}"]`);
      this._renderSlotEmpty(slot, index);
      this._updateBadges();
      this._onSlotChange();
      this._validateInputIfVisible();
    }

    _renderSlotFilled(slotEl, index, charm) {
      slotEl.classList.add('is-filled');

      // Locked charms drop the drag affordance
      const locked = this._preferredSlotFor(charm._catIdx) === index;
      slotEl.classList.toggle('is-locked', locked);
      slotEl.draggable = !locked;

      const displayTitle = charm.itemTitle || charm.title;
      const initial = displayTitle.charAt(0);

      if (charm.imageUrl) {
        slotEl.innerHTML = `
          <div class="rs-charm__slot-visual">
            <img src="${charm.imageUrl}" alt="${esc(displayTitle)}"
              onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'rs-charm__slot-fallback',textContent:'${initial}'}))">
          </div>
          <span class="rs-charm__slot-label">${charm.title}</span>
          <button type="button" class="rs-charm__slot-remove" data-remove="${index}" aria-label="Remove ${charm.title}"><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>`;
      } else {
        slotEl.innerHTML = `
          <div class="rs-charm__slot-visual">
            <span class="rs-charm__slot-fallback">${initial}</span>
          </div>
          <span class="rs-charm__slot-label">${charm.title}</span>
          <button type="button" class="rs-charm__slot-remove" data-remove="${index}" aria-label="Remove ${charm.title}"><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>`;
      }

      slotEl.querySelector('[data-remove]').addEventListener('click', (e) => {
        e.stopPropagation();
        this._removeFromSlot(index);
      });
    }

    _renderSlotEmpty(slotEl, index) {
      slotEl.classList.remove('is-filled', 'is-locked');
      slotEl.draggable = true;
      slotEl.innerHTML = `
        <div class="rs-charm__slot-visual">
          <span class="rs-charm__slot-fallback">Blank</span>
        </div>
        <span class="rs-charm__slot-label">${this._slotLabel} #${index + 1}</span>`;
    }

    _updateBadges() {
      const countsByVariant = {};
      const countsByTitle = {};
      for (const s of this.slots) {
        if (!s) continue;
        if (s.variantId) {
          const vid = String(s.variantId);
          countsByVariant[vid] = (countsByVariant[vid] || 0) + 1;
        } else {
          const title = s.itemTitle || s.title || '';
          countsByTitle[title] = (countsByTitle[title] || 0) + 1;
        }
      }

      this._root.querySelectorAll('.rs-charm__grid-item-count').forEach(el => el.remove());

      this._root.querySelectorAll('.rs-charm__grid-item').forEach(btn => {
        const vid = btn.dataset.variantId || '';
        const count = vid ? (countsByVariant[vid] || 0) : (countsByTitle[btn.dataset.title || ''] || 0);
        btn.classList.toggle('is-selected', count > 0);
        if (count > 1) {
          btn.insertAdjacentHTML('beforeend',
            `<span class="rs-charm__grid-item-count">${count}</span>`);
        }
      });
    }

    _validateInputIfVisible() {
      const cat = this.config.categories[this._activeCat];
      if (cat?.type === 'picker-with-input') {
        const panel = this._panelsWrap.querySelector(`[data-panel="${this._activeCat}"]`);
        if (panel) this._validateInput(panel);
      }
    }

    /* ── Drag & Drop ───────────────────────────── */

    _initDragDrop() {
      this._slotList.addEventListener('dragstart', (e) => {
        const slot = e.target.closest('[data-slot]');
        if (!slot) return;
        this._dragSrcIdx = parseInt(slot.dataset.slot, 10);
        slot.classList.add('is-dragging');
        document.body.classList.add('rs-charm-dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(this._dragSrcIdx));
      });

      this._slotList.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        this._slotList.querySelectorAll('.rs-charm__slot').forEach(s => s.classList.remove('is-drag-over'));
        const slot = e.target.closest('[data-slot]');
        if (slot && !slot.classList.contains('is-locked')) slot.classList.add('is-drag-over');
      });

      this._slotList.addEventListener('dragend', () => {
        this._slotList.querySelectorAll('.rs-charm__slot').forEach(s =>
          s.classList.remove('is-dragging', 'is-drag-over'));
        document.body.classList.remove('rs-charm-dragging');
        this._dragSrcIdx = null;
      });

      this._slotList.addEventListener('drop', (e) => {
        e.preventDefault();
        this._slotList.querySelectorAll('.rs-charm__slot').forEach(s =>
          s.classList.remove('is-dragging', 'is-drag-over'));
        const target = e.target.closest('[data-slot]');
        if (!target) return;
        const destIdx = parseInt(target.dataset.slot, 10);
        if (this._dragSrcIdx === null || this._dragSrcIdx === destIdx) return;
        this._swapSlots(this._dragSrcIdx, destIdx);
        this._dragSrcIdx = null;
      });

      this._initTouchDrag();
    }

    _initTouchDrag() {
      let ghost = null;
      let srcIdx = null;
      let startX = 0;
      let startY = 0;

      const getSlotAtPoint = (x, y) => {
        for (const el of document.elementsFromPoint(x, y)) {
          const slot = el.closest('[data-slot]');
          if (slot && this._slotList.contains(slot)) return slot;
        }
        return null;
      };

      this._slotList.addEventListener('touchstart', (e) => {
        const slot = e.target.closest('[data-slot]');
        if (!slot || !slot.classList.contains('is-filled')) return;
        if (slot.classList.contains('is-locked')) return;
        srcIdx = parseInt(slot.dataset.slot, 10);
        const { clientX, clientY } = e.touches[0];
        startX = clientX;
        startY = clientY;
      }, { passive: true });

      this._slotList.addEventListener('touchmove', (e) => {
        if (srcIdx === null) return;
        const { clientX, clientY } = e.touches[0];

        if (!ghost && (Math.abs(clientX - startX) > 10 || Math.abs(clientY - startY) > 10)) {
          const srcSlot = this._slotList.querySelector(`[data-slot="${srcIdx}"]`);
          ghost = srcSlot.cloneNode(true);
          ghost.className = 'rs-charm__slot rs-charm__slot--ghost';
          ghost.style.cssText = 'position:fixed;pointer-events:none;z-index:999;opacity:0.8;';
          document.body.appendChild(ghost);
          document.body.classList.add('rs-charm-dragging');
        }

        if (ghost) {
          e.preventDefault();
          ghost.style.left = `${clientX - 36}px`;
          ghost.style.top = `${clientY - 36}px`;
          this._slotList.querySelectorAll('.rs-charm__slot').forEach(s => s.classList.remove('is-drag-over'));
          const target = getSlotAtPoint(clientX, clientY);
          if (target && !target.classList.contains('is-locked')) target.classList.add('is-drag-over');
        }
      }, { passive: false });

      this._slotList.addEventListener('touchend', (e) => {
        if (ghost) { ghost.remove(); ghost = null; }
        document.body.classList.remove('rs-charm-dragging');
        if (srcIdx === null) return;
        this._slotList.querySelectorAll('.rs-charm__slot').forEach(s => s.classList.remove('is-drag-over'));
        const { clientX, clientY } = e.changedTouches[0];
        const target = getSlotAtPoint(clientX, clientY);
        if (target) {
          const destIdx = parseInt(target.dataset.slot, 10);
          if (destIdx !== srcIdx) this._swapSlots(srcIdx, destIdx);
        }
        srcIdx = null;
      });

      this._slotList.addEventListener('touchcancel', () => {
        if (ghost) { ghost.remove(); ghost = null; }
        document.body.classList.remove('rs-charm-dragging');
        srcIdx = null;
      });
    }

    _swapSlots(a, b) {
      // Locked charm stays put
      if (this._isSlotLocked(a) || this._isSlotLocked(b)) {
        this._flashSlot(this._isSlotLocked(b) ? b : a);
        return;
      }

      [this.slots[a], this.slots[b]] = [this.slots[b], this.slots[a]];

      for (const idx of [a, b]) {
        const slotEl = this._slotList.querySelector(`[data-slot="${idx}"]`);
        if (this.slots[idx]) {
          this._renderSlotFilled(slotEl, idx, this.slots[idx]);
        } else {
          this._renderSlotEmpty(slotEl, idx);
        }
      }

      // Slot order changed — the mobile sticky CTA need to know.
      this._onSlotChange();
    }

    /* ── Richtext Rendering ──────────────────────── */

    _richTextToHtml(raw) {
      try {
        const tree = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return this._renderNode(tree);
      } catch {
        return raw || '';
      }
    }

    _renderNode(node) {
      if (!node) return '';

      if (node.type === 'text') {
        let val = node.value || '';
        if (node.bold) val = `<strong>${val}</strong>`;
        if (node.italic) val = `<em>${val}</em>`;
        return val;
      }

      const children = (node.children || []).map(c => this._renderNode(c)).join('');

      switch (node.type) {
        case 'root': return children;
        case 'paragraph': return `<p>${children}</p>`;
        case 'heading': return `<h${node.level || 3}>${children}</h${node.level || 3}>`;
        case 'list':
          return node.listType === 'ordered'
            ? `<ol>${children}</ol>`
            : `<ul>${children}</ul>`;
        case 'list-item': return `<li>${children}</li>`;
        case 'link': return `<a href="${node.url || '#'}" target="${node.target || '_self'}">${children}</a>`;
        default: return children;
      }
    }

    /* ── MTO Validation ────────────────────────── */

    _getMtoRules(catIdx, subIdx) {
      const cat = this.config.categories[catIdx];
      if (!cat) return null;
      const sub = cat.subcategories[subIdx];
      return sub?.mto || cat.mto || null;
    }

    _applyMto(mto, panel) {
      const input = panel.querySelector('[data-text-input]');
      if (!input) return;

      const maxLen = mto.max?.[0] || 100;
      const minLen = mto.min?.[0] || 0;
      const restrictions = mto.restrictions?.[0] || '';
      const lettercase = mto.cases?.[0] || 'none';

      input.setAttribute('maxlength', maxLen);
      input.setAttribute('minlength', minLen);

      if (restrictions) {
        const parts = restrictions
          .replace(/Space/g, ' ')
          .replace(/\^/g, '\\^')
          .replace(/-/g, '\\-')
          .split(', ');
        input.setAttribute('pattern', `[${parts.join('')}]*`);
      } else {
        input.removeAttribute('pattern');
      }

      input.classList.remove('rs-charm__input--uppercase', 'rs-charm__input--lowercase');
      if (this._caseHandler) {
        input.removeEventListener('input', this._caseHandler);
        this._caseHandler = null;
      }

      if (lettercase === 'uppercase' || lettercase === 'lowercase') {
        input.classList.add(`rs-charm__input--${lettercase}`);
        this._caseHandler = () => {
          input.value = lettercase === 'uppercase' ? input.value.toUpperCase() : input.value.toLowerCase();
          this._validateInput(panel);
        };
        input.addEventListener('input', this._caseHandler);
      }

      const maxEl = panel.querySelector('[data-max-chars]');
      if (maxEl) maxEl.textContent = maxLen;

      const subtext = panel.querySelector('.rs-charm__subtext');
      if (subtext) {
        const existing = subtext.querySelector('.rs-charm__info');
        if (existing) existing.remove();

        if (restrictions) {
          let caseNote = '';
          if (lettercase === 'uppercase') caseNote = '<p class="rs-charm__info-note">Alphabetical letters will automatically uppercase.</p>';
          else if (lettercase === 'lowercase') caseNote = '<p class="rs-charm__info-note">Alphabetical letters will automatically lowercase.</p>';

          const infoEl = document.createElement('div');
          infoEl.className = 'rs-charm__info';
          infoEl.innerHTML = `
            <button type="button" class="rs-charm__info-trigger">
              <span class="rs-charm__info-text">View limitations</span>
              <span class="rs-charm__info-icon">
                <svg aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8.00016 10.6667V8.00004M8.00016 5.33337H8.00683M14.6668 8.00004C14.6668 11.6819 11.6821 14.6667 8.00016 14.6667C4.31826 14.6667 1.3335 11.6819 1.3335 8.00004C1.3335 4.31814 4.31826 1.33337 8.00016 1.33337C11.6821 1.33337 14.6668 4.31814 14.6668 8.00004Z" stroke="#444444" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
            <div class="rs-charm__info-popup" hidden>
              <button type="button" class="rs-charm__info-close" aria-label="Close">&times;</button>
              <p class="rs-charm__info-heading">Permitted characters and symbols</p>
              ${caseNote}
              <p class="rs-charm__info-subtext">Input is currently limited to the following characters:</p>
              <p class="rs-charm__info-chars">${this._renderRestrictionChars(restrictions)}</p>
            </div>`;

          const trigger = infoEl.querySelector('.rs-charm__info-trigger');
          const popup = infoEl.querySelector('.rs-charm__info-popup');
          const closeBtn = infoEl.querySelector('.rs-charm__info-close');

          trigger.addEventListener('click', () => {
            const isOpen = !popup.hidden;
            popup.hidden = isOpen;
            trigger.classList.toggle('is-active', !isOpen);
          });
          closeBtn.addEventListener('click', () => {
            popup.hidden = true;
            trigger.classList.remove('is-active');
          });

          subtext.appendChild(infoEl);
        }
      }

      this._validateInput(panel);
    }

    _renderRestrictionChars(restrictions) {
      if (!restrictions) return '';
      let result = restrictions;

      // Only loop through icons (small list) and replace their utfChar with img HTML
      if (this._iconsData) {
        for (const icon of this._iconsData) {
          if (!icon.utfChar) continue;
          const imgSrc = icon.icon || icon.imageUrl || '';
          if (imgSrc) {
            const imgHtml = `<span class="rs-mto__info-motif" title="${esc(icon.title || '')}"><img src="${imgSrc}" alt="${esc(icon.title || '')}" class="rs-mto__info-motif-img" width="20" height="20" loading="lazy"></span>`;
            result = result.replaceAll(icon.utfChar, imgHtml);
          }
        }
      }

      return result;
    }

    _validateInput(panel) {
      const addBtn = panel.querySelector('[data-text-add]');
      const input = panel.querySelector('[data-text-input]');
      if (!addBtn || !input) return;

      addBtn.disabled = !input.value.trim() || !input.checkValidity() || !this.slots.includes(null) || !this._pendingCharm;
    }

    /* ── Blank Slot Confirmation ───────────────── */

    _showBlankAlert(message) {
      const dialog = this._getConfirmDialog();
      dialog.querySelector('[data-dialog-body]').innerHTML = `<p>${message}</p>`;
      const actions = dialog.querySelector('[data-dialog-actions]');
      actions.innerHTML = '<button type="button" class="btn btn--primary btn--solid" data-close>OK</button>';
      actions.querySelector('[data-close]').addEventListener('click', () => this._closeConfirmDialog(), { once: true });
      this._openConfirmDialog();
    }

    _confirmBlankSlots(blankIndices) {
      return new Promise(resolve => {
        const dialog = this._getConfirmDialog();
        const count = blankIndices.length;
        const chips = blankIndices.map(i => `<span class="rs-modal__chip">Charm #${i}</span>`).join('');
        dialog.querySelector('[data-dialog-body]').innerHTML = `
          <div class="rs-modal__notice">
            <svg class="rs-modal__notice-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 11.5v5m0-8.99.01-.011M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/></svg>
            <strong class="rs-modal__notice-title body-medium">${count} blank ${count > 1 ? 'slots' : 'slot'} remaining</strong>
          </div>
          <div class="rs-modal__chips">${chips}</div>
          <p class="rs-modal__notice-msg">These slots will be left empty. Would you like to proceed?</p>`;

        const actions = dialog.querySelector('[data-dialog-actions]');
        actions.innerHTML = `
          <button type="button" class="btn btn--primary btn--outline" data-cancel>Go Back</button>
          <button type="button" class="btn btn--primary btn--solid" data-confirm>Yes, Proceed</button>`;

        let settled = false;
        const done = (result) => {
          if (settled) return;
          settled = true;
          this._closeConfirmDialog();
          resolve(result);
        };
        actions.querySelector('[data-cancel]').addEventListener('click', () => done(false), { once: true });
        actions.querySelector('[data-confirm]').addEventListener('click', () => done(true), { once: true });
        dialog.addEventListener('close', () => done(false), { once: true });
        this._openConfirmDialog();
      });
    }

    _getConfirmDialog() {
      if (!this._confirmDialogBound) {
        this._confirmDialog.addEventListener('click', (e) => {
          if (e.target === this._confirmDialog) this._closeConfirmDialog();
        });
        this._confirmDialogBound = true;
      }
      return this._confirmDialog;
    }

    _openConfirmDialog() {
      this._confirmDialog.showModal();
    }

    _closeConfirmDialog() {
      this._confirmDialog.close();
    }
  };
})();
